import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { merge } from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { throwError, BehaviorSubject, Observable, EMPTY } from 'rxjs';
import {
  catchError,
  expand,
  filter,
  finalize,
  map,
  shareReplay,
  skip,
  take,
  tap,
} from 'rxjs/operators';
import { v4 } from 'uuid';

import { ActiveCustomerService } from '@digital-platform/customers/services';
import { WasteProfileFlat } from '@digital-platform/manifests/domain';
import {
  ErrorService,
  FeatureFlagsService,
} from '@digital-platform/shared/services';
import { SignaturesFacadeService } from '@digital-platform/sign-now/services';
import { LoginSessionFacadeService } from '@digital-platform/users/services';
import {
  ChangeRequestDetails,
  DeepwellWasteProfileModel,
  ExpressWasteProfileModel,
  FindWasteProfileSearchApiResponse,
  FindAllApiResponse,
  Hazmat,
  OfflineSubmissionType,
  RecertificationDetails,
  SpecialWasteProfile,
  SpecialWasteProfileModel,
  TruxDescription,
  WasteProfile,
  WasteProfileModel,
  WasteProfileSearch,
} from '@digital-platform/waste-profiles/domain';
import { WasteProfilesService } from '../waste-profiles/waste-profiles.service';
import { WasteProfileCardsService } from '../waste-profiles-cards/waste-profile-cards.service';

@Injectable({
  providedIn: 'root',
})
export class WasteProfilesFacadeService {
  private activeNotifier = new BehaviorSubject<WasteProfile>(null);
  private loading = new BehaviorSubject<boolean>(false);
  private loaded = new BehaviorSubject<boolean>(false);
  private activeOfflineSubmissionTypeNotifier =
    new BehaviorSubject<OfflineSubmissionType>(null);
  private activeOfflineApprovalFlagNotifier = new BehaviorSubject<boolean>(
    null
  );
  private activeOfflineExistsFlagNotifier = new BehaviorSubject<boolean>(null);
  private selectedCustomer: string;
  private loadingCount = 0;
  private hazmatNotifier = new BehaviorSubject<Hazmat[]>([]);
  private hazmatLoaded = false;
  private hazmatLoading = false;
  private searchLoopId = '';
  // services can be injected like this as well, so we may want to do it this way going forward
  private wasteProfileCardsService = inject(WasteProfileCardsService);
  private findRequests: { [key: string]: Observable<WasteProfile> } = {};
  private activeCustomerService = inject(ActiveCustomerService);

  public activeWasteProfile$ = this.activeNotifier.asObservable();
  public activeOfflineSubmissionType$: Observable<OfflineSubmissionType> =
    this.activeOfflineSubmissionTypeNotifier.asObservable();
  public activeOfflineApprovalFlag$: Observable<boolean> =
    this.activeOfflineApprovalFlagNotifier.asObservable();
  public activeOfflineExistsFlag$: Observable<boolean> =
    this.activeOfflineExistsFlagNotifier.asObservable();
  public loading$ = this.loading.asObservable();
  public loaded$ = this.loaded.asObservable();

  constructor(
    private readonly router: Router,
    private readonly logger: NGXLogger,
    private readonly service: WasteProfilesService,
    private readonly errorService: ErrorService,
    private readonly loginFacade: LoginSessionFacadeService,
    private readonly signaturesFacade: SignaturesFacadeService,
    private readonly featureFlags: FeatureFlagsService
  ) {
    this.activeCustomerService.selectedCustomer$.subscribe((customer) => {
      this.selectedCustomer = customer;
    });
  }

  public setActive(wasteProfile?: WasteProfile) {
    this.logger.info('Set active Waste Profile', { wasteProfile });

    this.activeNotifier.next(wasteProfile);
  }

  public persist(): Promise<void> {
    const value = this.activeNotifier.value;

    if (value) {
      return this.upsert(value);
    }

    return Promise.resolve();
  }

  public patch(patch: Partial<WasteProfile>) {
    const value = this.activeNotifier.value;

    if (value) {
      this.activeNotifier.next(this.mergeUpdates(value, patch));
      this.wasteProfileCardsService.updateCard(
        this.mergeUpdates(value, patch) as SpecialWasteProfile
      );
    }
  }

  private overwriteArrays<T>(obj: T, update: Partial<T>): T {
    let merged = { ...obj };
    for (const [key, value] of Object.entries(update)) {
      if (Array.isArray(value)) {
        // overwrite an array value in the merged object
        merged = {
          ...merged,
          [key]: value,
        };
      }
    }

    return merged;
  }

  public findAll(): Observable<WasteProfileFlat[]> {
    this.startLoading();

    return this.service.findAll().pipe(
      map((response: FindAllApiResponse) => {
        const wasteProfiles = response.Items.filter(this.wpFilter).map(
          (item) => ({
            id: item.id,
            selected: false,
            generatorName: item.generator?.name,
            profileNumber: item.number,
            profileName: item.name,
            envelopeId: '',
            approvedDate: item.approvedDate,
            expirationDate: item.expiryDate,
            destination: item.destination?.name,
            destinationAddress: item.destination?.address,
            siteAddress: item.generator?.siteAddress,
            mailingAddress: item.generator?.generatorAddress,
            epaId: item.generator?.epaId,
            contactPhone: item.generator?.contact?.phone,
          })
        );
        return wasteProfiles;
      }),
      tap(() => {
        this.stopLoading();
      })
    );
  }

  // private wpFilter = (wp: WasteProfile): boolean => true;

  // Return all except waste profiles with 'Expired' status
  private wpFilter = (wp: WasteProfile): boolean => {
    const validStatus =
      [
        'Approved',
        'Expiring',
        // 'Action Required',
        // 'Incomplete',
        // 'Not Submitted',
        // 'Pending Signature',
        // 'Submitted',
        // 'Expired'
      ].indexOf(wp.status) !== -1;

    return validStatus ? wp.expiryDate > new Date() : false;
  };

  public mergeUpdates<T extends WasteProfile>(
    wasteProfile: T,
    ...updates: Partial<T>[]
  ): T {
    let merged: T = { ...wasteProfile };
    updates.forEach((update) => {
      merged = merge(merged, update); // deeply updates the model

      merged = this.overwriteArrays(merged, update);
    });

    return merged;
  }

  public create(type?: WasteProfile['type']): WasteProfile {
    if (!this.selectedCustomer) {
      throw new Error('Customer name is required');
    }
    let created: WasteProfile;
    const addCustomerToCreated = (object) => ({
      ...object,
      type,
      customerName: this.selectedCustomer,
    });
    switch (type) {
      case 'special-waste':
        created = JSON.parse(
          JSON.stringify(SpecialWasteProfileModel.DEFAULT_CONFIG)
        );
        created = addCustomerToCreated(created);
        break;
      case 'deepwell':
        created = JSON.parse(
          JSON.stringify(DeepwellWasteProfileModel.DEFAULT_CONFIG)
        );
        created = addCustomerToCreated(created);
        break;
      case 'express':
        created = JSON.parse(
          JSON.stringify(ExpressWasteProfileModel.DEFAULT_CONFIG)
        );
        created = addCustomerToCreated(created);
        break;
      default:
        created = JSON.parse(JSON.stringify(WasteProfileModel.DEFAULT_CONFIG));
        created = addCustomerToCreated(created);
    }

    this.logger.info('Created new Waste Profile', { created });

    this.setActive(created);

    return created;
  }

  public upsert<T extends WasteProfile>(
    wasteProfile: T,
    ...updates: Partial<T>[]
  ): Promise<void> {
    let merged: T = { ...wasteProfile };
    updates.forEach((update) => {
      merged = this.mergeUpdates(merged, update);
    });
    if (wasteProfile.id) {
      return this.update(wasteProfile.id, merged);
    } else {
      return this.add(merged);
    }
  }

  public add(newWasteProfile: WasteProfile): Promise<void> {
    this.errorService.clearErrors();
    this.startLoading();

    // we need `shareReplay` here b/c HttpClient's cold observables make new
    // requests for each`subscribe` call(which includes`toPromise` which
    // internally calls`subscribe`)
    const serviceCall = this.service
      .create(newWasteProfile)
      .pipe(shareReplay());

    serviceCall.subscribe(
      (wasteProfile) => {
        this.setActive(wasteProfile);
      },
      (error) => {
        this.errorService.raiseError(error);
      },
      () => this.stopLoading()
    );

    return serviceCall.toPromise().then(() => {});
  }

  public update(id: string, wasteProfile: WasteProfile): Promise<void> {
    this.errorService.clearErrors();
    this.startLoading();

    // we need `shareReplay` here b/c HttpClient's cold observables make new
    // requests for each`subscribe` call(which includes`toPromise` which
    // internally calls`subscribe`)
    const serviceCall = this.service
      .update(id, wasteProfile)
      .pipe(shareReplay());

    serviceCall.subscribe(
      (updated) => {
        this.setActive(updated);
        this.wasteProfileCardsService.updateCard(updated);
      },
      (error) => {
        this.errorService.raiseError(error);
        this.stopLoading();
      },
      () => this.stopLoading()
    );

    return serviceCall.toPromise().then(() => {});
  }

  public delete(id: string) {
    this.startLoading();
    this.service.delete(id).subscribe(
      (updated) => {
        this.wasteProfileCardsService.deleteCard(updated.id);
      },
      (error) => {
        this.errorService.raiseError(error);
        this.stopLoading();
      },
      () => this.stopLoading()
    );
  }

  public activate(
    id: string,
    wasteProfile: WasteProfile
  ): Observable<WasteProfile> {
    this.startLoading();

    return this.service.activate(id, wasteProfile).pipe(
      tap((updated) => {
        this.wasteProfileCardsService.deleteCard(updated.id);
      }),
      catchError((error) => {
        this.errorService.raiseError(error);
        return throwError(() => error);
      }),
      finalize(() => this.stopLoading())
    );
  }

  public checkUserSignatureAuth(id: string): Observable<boolean> {
    this.startLoading();
    return this.service.checkUserSignatureAuth(id).pipe(
      catchError((error) => {
        this.errorService.raiseError(error);
        return throwError(error);
      }),
      finalize(() => this.stopLoading()),
      shareReplay(1)
    );
  }

  public recallSignature(id: string) {
    this.startLoading();
    this.service.recallSignature(id).subscribe(
      (updated) => {
        this.wasteProfileCardsService.updateCard(updated);
      },
      (error) => {
        this.errorService.raiseError(error);
        this.stopLoading();
      },
      () => this.stopLoading()
    );
  }

  public cancelChange(id: string) {
    this.startLoading();
    this.service.cancelChange(id).subscribe(
      (updated) => {
        this.wasteProfileCardsService.updateCard(updated);
      },
      (error) => {
        this.errorService.raiseError(error);
        this.stopLoading();
      },
      () => this.stopLoading()
    );
  }
  public chooseSigner(type: string, id: string, name: string, email: string) {
    this.startLoading();
    this.service.chooseSigner(type, id, name, email).subscribe(
      (updated) => {
        this.setActive(<WasteProfile>updated);
      },
      (error) => {
        this.errorService.raiseError(error);
      },
      () => this.stopLoading()
    );
  }

  public sendDesignatedSignerEmails(type: string, id: string) {
    this.startLoading();
    return this.service
      .sendDesignatedSignerEmails(type, id)
      .pipe(
        catchError((error) => {
          this.errorService.raiseError(error);
          return throwError(error);
        }),
        finalize(() => this.stopLoading()),
        shareReplay(1)
      )
      .subscribe(); // christ, remember to subscribe to the observable
  }

  public sendSignerInfo(id: string, name: string, email: string) {
    this.startLoading();
    this.service.sendSignerInfo(id, name, email).subscribe(
      (updated) => {
        this.setActive(updated);
      },
      (error) => {
        this.errorService.raiseError(error);
      },
      () => this.stopLoading()
    );
  }

  public recertify(id: string, recertificationDetails: RecertificationDetails) {
    this.errorService.clearErrors();
    this.startLoading();
    this.service.recertify(id, recertificationDetails).subscribe(
      (updated) => {
        this.setActive(updated);
      },
      (error) => {
        this.errorService.raiseError(error);
      },
      () => this.stopLoading()
    );
  }

  public change(id: string, changeRequestDetails: ChangeRequestDetails) {
    this.errorService.clearErrors();
    this.startLoading();
    this.service.change(id, changeRequestDetails).subscribe(
      (updated) => {
        this.setActive(updated);
      },
      (error) => {
        this.errorService.raiseError(error);
      },
      () => this.stopLoading()
    );
  }

  public duplicate(wasteProfileId: string, showSplash = false) {
    this.errorService.clearErrors();
    if (showSplash) {
      this.router.navigate([`/waste-profiles/duplicate`], {
        skipLocationChange: true,
      });
    }

    this.startLoading();
    this.setActive(null);
    this.service.duplicate(wasteProfileId).subscribe(
      (wasteProfile) => {
        this.setActive(wasteProfile);
      },
      (error) => {
        this.errorService.raiseError(error);
      },
      () => this.stopLoading()
    );
  }

  public findById(wasteProfileId: string): Observable<WasteProfile | never> {
    this.errorService.clearErrors();
    this.startLoading();
    if (this.activeNotifier.getValue()?.id !== wasteProfileId) {
      // reset the active profile if we're loading a different one
      this.setActive(null);
    }

    return this._findById(wasteProfileId).pipe(
      tap((wasteProfile) => {
        this.wasteProfileCardsService.updateCard(wasteProfile);
        this.setActive(wasteProfile);
      })
    );
  }

  public findOrRefreshById(
    wasteProfileId: string
  ): Observable<WasteProfile | never> {
    const current = this.activeNotifier.getValue();

    this.errorService.clearErrors();
    this.startLoading();
    if (current?.id !== wasteProfileId) {
      // reset the active profile if we're loading a different one
      this.setActive(null);
    }

    return this._findById(wasteProfileId).pipe(
      tap((wasteProfile) => {
        if (
          current?.id === wasteProfile.id &&
          current?.version >= wasteProfile.version
        ) {
          this.logger.info(
            'Waste Profile version is newer or equal to server version, skipping updating state',
            {
              id: wasteProfile.id,
              currentVersion: current?.version,
              serverVersion: wasteProfile.version,
            }
          );
          return;
        }

        this.setActive(wasteProfile);
      })
    );
  }

  // TODO: We can potentially read from the state colelction, and fetch only if necessary
  // There are some open design questions regarding maintaining this state.
  private _findById(wasteProfileId: string): Observable<WasteProfile | never> {
    this.findRequests[wasteProfileId] = this.service
      .findById(wasteProfileId)
      .pipe(
        catchError((error) => {
          this.errorService.raiseError(error);

          return throwError(error);
        }),
        finalize(() => {
          // remove the pending call from the cache upon completion
          const { [wasteProfileId]: request, ...findRequests } =
            this.findRequests;
          this.findRequests = findRequests;
          this.stopLoading();
        })
      );

    return this.findRequests[wasteProfileId];
  }

  public search(
    searchBy: {
      key: string;
      value: any;
    }[]
  ): Observable<WasteProfileSearch[]> {
    this.searchLoopId = v4();
    this.errorService.clearErrors();
    return this.searchLoop(searchBy).pipe(
      map(
        (response: FindWasteProfileSearchApiResponse): WasteProfileSearch[] =>
          response.Items
      )
    );
  }

  public searchLoop(searchBy) {
    return this.service.search(searchBy, this.searchLoopId).pipe(
      expand((response) => {
        if (!response.LastEvaluatedKey) {
          // we got an empty response, nothing to do here
          // restart the search loop id
          this.searchLoopId = '';

          // return an empty observable to stop the loop
          return EMPTY;
        }

        return this.service.search(
          searchBy,
          this.searchLoopId,
          response?.LastEvaluatedKey
        );
      })
    );
  }

  public get hazmatMaster$(): Observable<Hazmat[]> {
    if (!this.hazmatLoaded && !this.hazmatLoading) {
      this.hazmatLoading = true;
      this.service.getHazmats().subscribe((hazmats) => {
        this.hazmatLoading = false;
        this.hazmatNotifier.next(hazmats);
        this.hazmatLoaded = true;
      });
    }

    return this.hazmatNotifier.asObservable();
  }

  /**
   * @deprecated - Not implemented for searching server side
   * @param searchBy
   * @returns
   */
  public searchWithHazmats(
    searchBy: {
      key: string;
      value: any;
    }[]
  ): Observable<any[]> {
    this.errorService.clearErrors();
    return this.service?.searchWithHazmats(searchBy).pipe(
      tap(() => {
        this.stopLoading();
      })
    );
  }

  public getSigningLink(wasteProfileId: string) {
    this.errorService.clearErrors();
    this.signaturesFacade.pdfLink$
      .pipe(
        skip(1), // ignore any current value
        filter(
          (link) =>
            link &&
            link.dataItemType === 'wasteProfile' &&
            link.dataItemId === wasteProfileId
        ),
        take(1)
      )
      .subscribe(() => {
        // updates the UI model after getting the signing link
        this.findById(wasteProfileId).subscribe();
      });

    this.signaturesFacade.getPdf('wasteProfile', wasteProfileId);
  }

  public getTruxMaterial(): Observable<TruxDescription[]> {
    this.errorService.clearErrors();
    return this.service.getTruxMaterial().pipe(
      tap(() => {
        this.stopLoading();
      })
    );
  }

  public setActiveOfflineSubmissionType(type: OfflineSubmissionType) {
    this.logger.info('Set active Offline Submission Type', { type });
    this.activeOfflineSubmissionTypeNotifier.next(type);
  }

  public setActiveOfflineApprovalFlag(approved: boolean) {
    this.logger.info('Set active Offline Approval Flag', { approved });
    this.activeOfflineApprovalFlagNotifier.next(approved);
  }

  public setActiveOfflineExistsFlag(exists: boolean) {
    this.logger.info('Set active Offline Exists Flag', { exists });
    this.activeOfflineExistsFlagNotifier.next(exists);
  }

  private startLoading() {
    this.loadingCount++;
    if (this.loadingCount > 0) {
      this.loading.next(true);
    }
  }

  private stopLoading() {
    this.loadingCount--;
    if (this.loadingCount <= 0) {
      this.loading.next(false);
    }
  }
}
